// MONITORING-P1 in the in-memory API (docs/design/2026-11-01-monitoring-in-companyos.md §3-4;
// packages/schema contracts/api/monitoring.ts): a run's telemetry, the Stop-on-host request, the
// monitoring alerts and the operator's guard rules, with the real routes' rules —
// - `runs.telemetry` / `runs.requestStop`: whoever may view the run (the twins' `runs.get`
//   decides, so a run another twin holds is served the same way); a stop on an ended run is 409
//   `run_state_conflict`; a second press returns the live request;
// - every started run has a carousel job's events (wake pushed, checked out, tool steps, stages,
//   slide scores, a redo, cost, delivery): a live run is mid-render, an ended one delivered;
//   the job card is folded from those events, as the API folds `monitor_job`;
// - the twin plays the bridge by time: a stop is picked up 20 s after it was asked and acked
//   5 s later (`answerStop` answers it at once); unpicked after 5 minutes it expires;
// - alerts: managers only, the `agents` add-on on; seeded per company from its runs (a stalled
//   render, a stuck run, an acked post storm, a resolved guard trip); `state=active` (the
//   default) is open + acked;
// - guards: platform admins only (the operator console's rule); two built-in rules (their
//   pattern fixed) and a new rule always starts as `alert`;
// - MONITORING-P2: the fleet (managers, the add-on on) — the first DE heard from on `copy-11`,
//   the second on `copy-12` with telemetry off, the rest never — and the budget incidents
//   (managers; core, no add-on gate) seeded for the first two DEs.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  problem,
  validation,
} from "./fake";
import { COMPANY_IDS, uid } from "./fake-data";
import type { MeResponse } from "./types";

type Telemetry = OperationOutput<"runs.telemetry">;
type Event = Telemetry["events"][number];
type Job = Telemetry["jobs"][number];
type Stop = Telemetry["stop_requests"][number];
type Alert = Telemetry["alerts"][number];
type Guard = OperationOutput<"operator.guards.list">["data"][number];
type FleetRow = OperationOutput<"monitoring.fleet">["data"][number];
type Incident = OperationOutput<"monitoring.budgetIncidents">["data"][number];

/** The DE fields the fleet reads (the twins' seeded DEs). */
export type FakeDe = {
  membership_id: string;
  display_name: string;
  seat_id: string | null;
  seat_title: string | null;
};

export const FAKE_MONITORING_ROUTES = [
  "runs.telemetry",
  "runs.requestStop",
  "monitoring.alerts.list",
  "monitoring.alerts.update",
  "monitoring.fleet",
  "monitoring.budgetIncidents",
  "operator.guards.list",
  "operator.guards.create",
  "operator.guards.update",
] as const satisfies readonly RouteId[];

/** The run fields the twin reads (`runs.get` as the caller sees it). */
export type MonitoredRun = {
  id: string;
  membership_id: string;
  state: string;
  started_at: string | null;
  created_at: string;
};

export type MonitoringDeps = {
  now: () => Date;
  agentsOn: (p: Principal) => boolean;
  /** The run as `runs.get` answers the caller, or the refusal (404, 403). */
  runFor: (ctx: Ctx, id: string) => MonitoredRun | Response;
  /** Every run the twins hold for a company (seeds the alerts). */
  runsOf: (companyId: string) => readonly MonitoredRun[];
  /** MONITORING-P2: the company's DEs, in roster order (the fleet, the budget incidents). */
  desOf: (companyId: string) => readonly FakeDe[];
  /** null: a platform admin; else the operator console's refusal. */
  refuseOperator: (me: MeResponse) => Response | null;
};

const ENDED = new Set(["completed", "failed", "cancelled"]);
const PICK_AFTER_MS = 20_000;
const ACK_AFTER_MS = 5_000;
const EXPIRE_MS = 5 * 60_000;
const SLIDES = 7;
/** The carousel critic's per-slide scores (slide 4 fails once, redone). */
const SCORES = [8.6, 8.1, 7.9, 6.1, 8.4, 7.7, 8.8];

export const jobIdOf = (runId: string) => `carousel-${runId.slice(-8)}`;

/** Folds a job's `job.*` events into its card, as the API folds `monitor_job`. */
export function foldJob(events: readonly Event[], run: MonitoredRun): Job | null {
  const mine = events.filter((e) => e.job_id && e.type.startsWith("job."));
  const first = mine[0];
  if (!first?.job_id) return null;
  const job: Job = {
    job_id: first.job_id,
    run_id: run.id,
    de_membership_id: run.membership_id,
    kind: null,
    status: "running",
    stage: null,
    stages: [],
    slides_total: null,
    slides_done: 0,
    slides: [],
    score_min: null,
    score_avg: null,
    redos: 0,
    cost_micros: 0,
    tokens_in: 0,
    tokens_out: 0,
    delivery: null,
    started_at: first.occurred_at,
    ended_at: null,
    last_event_at: first.occurred_at,
  };
  for (const e of mine) {
    const d = e.data as Record<string, unknown>;
    job.last_event_at = e.occurred_at;
    if (e.type === "job.started") {
      job.kind = typeof d.job_kind === "string" ? d.job_kind : null;
      job.slides_total = typeof d.slides_total === "number" ? d.slides_total : null;
    } else if (e.type === "job.stage_started") {
      job.stage = String(d.stage);
      job.stages.push({
        stage: String(d.stage),
        attempt: Number(d.attempt ?? 1),
        started_at: e.occurred_at,
        ended_at: null,
        ok: null,
        budget_s: typeof d.budget_s === "number" ? d.budget_s : null,
      });
    } else if (e.type === "job.stage_ended") {
      const s = job.stages.findLast((x) => x.stage === d.stage && x.ended_at === null);
      if (s) Object.assign(s, { ended_at: e.occurred_at, ok: d.ok === true });
    } else if (e.type === "job.slide_scored") {
      const slide = Number(d.slide);
      const row = job.slides.find((x) => x.slide === slide);
      const score = typeof d.score === "number" ? d.score : null;
      const critic = typeof d.critic === "string" ? d.critic : null;
      const pass = typeof d.pass === "boolean" ? d.pass : null;
      if (row) Object.assign(row, { score, critic, pass });
      else job.slides.push({ slide, score, critic, pass, redos: 0 });
    } else if (e.type === "job.redo") {
      job.redos += 1;
      const row = job.slides.find((x) => x.slide === Number(d.slide));
      if (row) row.redos += 1;
    } else if (e.type === "job.cost") {
      job.cost_micros += Number(d.micros ?? 0);
      job.tokens_in += Number(d.tokens_in ?? 0);
      job.tokens_out += Number(d.tokens_out ?? 0);
    } else if (e.type === "job.delivered") {
      job.delivery = {
        thread_id: typeof d.thread_id === "string" ? d.thread_id : null,
        files: typeof d.files === "number" ? d.files : null,
        post_ids: Array.isArray(d.post_ids) ? (d.post_ids as string[]) : [],
        at: e.occurred_at,
      };
    } else if (e.type === "job.done") {
      job.status = d.status === "ok" ? "done" : d.status === "cancelled" ? "cancelled" : "failed";
      job.ended_at = e.occurred_at;
      job.stage = null;
    }
  }
  const passed = job.slides.filter((s) => s.pass === true);
  job.slides_done = passed.length;
  const scores = job.slides.map((s) => s.score).filter((s): s is number => s !== null);
  if (scores.length) {
    job.score_min = Math.min(...scores);
    job.score_avg = Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100;
  }
  return job;
}

export function createMonitoringFake(deps: MonitoringDeps) {
  const events = new Map<string, Event[]>();
  const stops = new Map<string, Stop[]>();
  const alerts = new Map<string, Alert[]>();
  /** The runs a stop was asked of, as the caller saw them. */
  const seen = new Map<string, MonitoredRun>();
  let stopSeq = 0;
  let eventSeq = 0;
  let alertSeq = 0;
  let guardSeq = 0;
  let incidentSeq = 0;

  /** The carousel job's events for a started run (seeded once). */
  function seedEvents(run: MonitoredRun): Event[] {
    const have = events.get(run.id);
    if (have) return have;
    const list: Event[] = [];
    events.set(run.id, list);
    if (!run.started_at) return list;
    const t0 = Date.parse(run.started_at);
    const live = !ENDED.has(run.state);
    // A run that started minutes ago has its ~6 minutes of events squeezed into them: none
    // lies in the future.
    const scale = Math.min(1, Math.max(0.01, (deps.now().getTime() - t0 - 5_000) / 380_000));
    const job = jobIdOf(run.id);
    const thread = "C07MKTG-CAROUSEL";
    const seqs = { bridge: 0, job: 0 };
    const add = (
      atS: number,
      type: string,
      data: Record<string, unknown> = {},
      o: {
        source?: "bridge" | "job";
        level?: Event["level"];
        message?: string;
        job?: boolean;
      } = {},
    ) => {
      const source = o.source ?? (type.startsWith("job.") ? "job" : "bridge");
      const ms = t0 + Math.round(atS * 1000 * scale);
      const at = new Date(ms).toISOString();
      list.push({
        id: uid(0xe7e0, ++eventSeq),
        type,
        source,
        seq: seqs[source]++,
        level: o.level ?? "info",
        run_id: run.id,
        job_id: o.job || type.startsWith("job.") ? job : null,
        message: o.message ?? null,
        data,
        redactions: 0,
        occurred_at: at,
        received_at: new Date(ms + 1500 * scale).toISOString(),
      });
    };
    add(0, "run.wake_pushed", { subject: "Weekly carousel: 5 hiring myths" });
    add(4, "run.checked_out");
    add(9, "step.tool", { tool: "read_thread", duration_ms: 820, ok: true });
    add(14, "step.tool", { tool: "search_knowledge", duration_ms: 2_140, ok: true });
    add(20, "job.started", { job_kind: "carousel", slides_total: SLIDES });
    add(21, "job.stage_started", { stage: "copy", attempt: 1, budget_s: 300 });
    add(21 + 35, "job.cost", {
      model: "claude-sonnet",
      tokens_in: 6_200,
      tokens_out: 1_850,
      micros: 46_000,
    });
    add(60, "job.stage_ended", { stage: "copy", attempt: 1, ok: true });
    add(61, "job.stage_started", { stage: "design", attempt: 1, budget_s: 300 });
    add(150, "job.stage_ended", { stage: "design", attempt: 1, ok: true });
    add(151, "job.stage_started", { stage: "render", attempt: 1, budget_s: 600 });
    const scored = live ? 5 : SLIDES;
    for (let i = 0; i < scored; i++) {
      const slide = i + 1;
      const score = SCORES[i] ?? 8;
      add(170 + i * 25, "job.slide_scored", {
        slide,
        score,
        critic: "legibility",
        pass: score >= 7,
      });
      if (score < 7) {
        add(171 + i * 25, "job.redo", { slide, reason: "Headline too small to read on a phone" });
        add(185 + i * 25, "job.slide_scored", {
          slide,
          score: 8.2,
          critic: "legibility",
          pass: true,
        });
      }
    }
    add(330, "job.cost", { model: "gpt-image", tokens_in: 3_400, tokens_out: 0, micros: 182_000 });
    if (live) {
      add(
        360,
        "bridge.log",
        {},
        { level: "warn", message: "Render slower than usual: 2 of 7 slides left" },
      );
      add(372, "host.heartbeat", { extra: { load: 0.82 } }, { message: "Host still working" });
      return list;
    }
    if (run.state === "failed") {
      add(
        345,
        "job.stage_ended",
        { stage: "render", attempt: 1, ok: false },
        { level: "error", message: "Render service timed out" },
      );
      add(346, "job.done", { status: "failed" }, { level: "error" });
      return list;
    }
    add(350, "job.stage_ended", { stage: "render", attempt: 1, ok: true });
    add(352, "outbox.posted", { thread_id: thread, message_id: "1730800000.0001" });
    add(355, "job.delivered", {
      thread_id: thread,
      files: SLIDES,
      post_ids: ["1730800000.0001"],
    });
    add(356, "job.done", { status: run.state === "cancelled" ? "cancelled" : "ok" });
    add(358, "run.completed");
    return list;
  }

  /** Plays the bridge by time: pending → picked → acked; unpicked past 5 min → expired. */
  function advance(run: MonitoredRun): Stop[] {
    seen.set(run.id, run);
    const list = stops.get(run.id) ?? [];
    const now = deps.now().getTime();
    for (const s of list) {
      if (s.state === "pending" && now >= Date.parse(s.expires_at)) s.state = "expired";
      else if (s.state === "pending" && now >= Date.parse(s.requested_at) + PICK_AFTER_MS)
        Object.assign(s, {
          state: "picked",
          picked_at: new Date(Date.parse(s.requested_at) + PICK_AFTER_MS).toISOString(),
        });
      if (s.state === "picked" && s.picked_at && now >= Date.parse(s.picked_at) + ACK_AFTER_MS)
        ack(run, s, true, new Date(Date.parse(s.picked_at) + ACK_AFTER_MS));
    }
    return list;
  }

  function ack(run: MonitoredRun, s: Stop, ok: boolean, at: Date) {
    const latency = Math.max(0, at.getTime() - Date.parse(s.requested_at));
    Object.assign(s, {
      state: ok ? "acked" : "failed",
      picked_at: s.picked_at ?? at.toISOString(),
      acked_at: at.toISOString(),
      ack: {
        ok,
        method: ok ? "gateway" : "hermes_cli",
        latency_ms: latency,
        message: ok ? null : "The run had already left the gateway",
      },
    });
    const list = seedEvents(run);
    list.push({
      id: uid(0xe7e0, ++eventSeq),
      type: "stop.acked",
      source: "bridge",
      seq: list.filter((e) => e.source === "bridge").length,
      level: ok ? "info" : "warn",
      run_id: run.id,
      job_id: null,
      message: ok ? null : "Could not stop the run on the host",
      data: { stop_request_id: s.id, ok, method: s.ack?.method, latency_ms: latency },
      redactions: 0,
      occurred_at: at.toISOString(),
      received_at: at.toISOString(),
    });
  }

  function seedAlerts(companyId: string): Alert[] {
    const have = alerts.get(companyId);
    if (have) return have;
    const list: Alert[] = [];
    alerts.set(companyId, list);
    const runs = deps.runsOf(companyId).filter((r) => r.started_at);
    const live = runs.filter((r) => r.state === "running");
    const done = runs.filter((r) => r.state === "completed");
    const now = deps.now().getTime();
    const ago = (min: number) => new Date(now - min * 60_000).toISOString();
    const alert = (o: Partial<Alert> & Pick<Alert, "kind" | "summary">): Alert => ({
      id: uid(0xe7e2, ++alertSeq),
      severity: "warning",
      state: "open",
      de_membership_id: null,
      run_id: null,
      job_id: null,
      guard_rule_id: null,
      evidence: {},
      opened_at: ago(12),
      last_seen_at: ago(1),
      acked_by_membership_id: null,
      acked_at: null,
      resolved_at: null,
      resolved_how: null,
      ...o,
    });
    const [a, b] = live;
    if (a)
      list.push(
        alert({
          kind: "job_stalled",
          de_membership_id: a.membership_id,
          run_id: a.id,
          job_id: jobIdOf(a.id),
          summary: "Render stage open past its 10 min budget",
          evidence: { stage: "render", budget_s: 600 },
        }),
      );
    if (b)
      list.push(
        alert({
          kind: "stuck_run",
          severity: "critical",
          de_membership_id: b.membership_id,
          run_id: b.id,
          summary: "No event from the host for 14 min",
          evidence: { idle_s: 840 },
          opened_at: ago(4),
        }),
      );
    const [c, d] = done;
    if (c)
      list.push(
        alert({
          kind: "post_storm",
          de_membership_id: c.membership_id,
          run_id: c.id,
          summary: "7 posts in 10 min to one thread",
          evidence: { posts: 7, window_min: 10 },
          state: "acked",
          opened_at: ago(90),
          last_seen_at: ago(80),
          acked_at: ago(85),
        }),
      );
    if (d)
      list.push(
        alert({
          kind: "guard_tripped",
          severity: "critical",
          de_membership_id: d.membership_id,
          run_id: d.id,
          guard_rule_id: BUILTIN_BROWSER,
          summary: "Guard: a browser tool was called (browser_navigate)",
          evidence: { tool: "browser_navigate" },
          state: "resolved",
          opened_at: ago(300),
          last_seen_at: ago(300),
          resolved_at: ago(240),
          resolved_how: "resolved by a person",
        }),
      );
    return list;
  }

  const BUILTIN_BROWSER = uid(0xe7e3, 1);
  const created = new Date(Date.UTC(2026, 9, 30, 9)).toISOString();
  const guard = (o: Partial<Guard> & Pick<Guard, "id" | "match_kind" | "pattern">): Guard => ({
    company_id: null,
    de_membership_id: null,
    action: "alert",
    enabled: true,
    builtin: false,
    note: null,
    version: 1,
    created_by_user_id: null,
    updated_by_user_id: null,
    created_at: created,
    updated_at: created,
    ...o,
  });
  guardSeq = 3;
  const guards: Guard[] = [
    guard({
      id: BUILTIN_BROWSER,
      match_kind: "tool",
      pattern: "browser_*",
      action: "stop_and_alert",
      builtin: true,
      note: "Any browser tool stops the run",
    }),
    guard({
      id: uid(0xe7e3, 2),
      match_kind: "tool",
      pattern: "screen_*",
      action: "stop_and_alert",
      builtin: true,
      note: "Any screen tool stops the run",
    }),
    guard({
      id: uid(0xe7e3, 3),
      company_id: COMPANY_IDS.htva,
      match_kind: "thread",
      pattern: "C05CLIENT-ANNOUNCE",
      note: "No post to the client's announcement thread without approval",
    }),
  ];

  /**
   * MONITORING-P2: the fleet as the bridges reported it — the company's first DE on `copy-11`
   * (v2.8.0, telemetry on, heard from a minute ago), the second on `copy-12` with telemetry off
   * (its bridge only pulses), every other DE never heard from. Open alerts from the seeded ones.
   */
  function fleetOf(companyId: string): FleetRow[] {
    const now = deps.now().getTime();
    const ago = (min: number) => new Date(now - min * 60_000).toISOString();
    const open = seedAlerts(companyId).filter((a) => a.state !== "resolved");
    const live = deps
      .runsOf(companyId)
      .filter((r) => r.state === "queued" || r.state === "running");
    return deps.desOf(companyId).map((d, i) => {
      const mine = open.filter((a) => a.de_membership_id === d.membership_id);
      const heard = i === 0 ? 1 : i === 1 ? 7 : null;
      return {
        de_membership_id: d.membership_id,
        de_name: d.display_name,
        seat_id: d.seat_id,
        seat_title: d.seat_title,
        host: i === 0 ? "copy-11" : i === 1 ? "copy-12" : null,
        source: heard === null ? null : "bridge",
        bridge_version: i === 0 ? "2.8.0" : i === 1 ? "2.7.1" : null,
        de_keys: i === 0 ? ["marketing-director", "copy-11-ops"] : i === 1 ? ["sales-lead"] : [],
        telemetry_on: i === 0 ? true : i === 1 ? false : null,
        uptime_s: i === 0 ? 86_400 : i === 1 ? 3_600 : null,
        last_heartbeat_at: heard === null ? null : ago(heard),
        last_event_at: heard === null ? null : ago(heard),
        last_event_type: heard === null ? null : "bridge.heartbeat",
        live_runs: live.filter((r) => r.membership_id === d.membership_id).length,
        open_alerts: mine.length,
        open_alert_kinds: [...new Set(mine.map((a) => a.kind))].sort(),
        critical: mine.some((a) => a.severity === "critical"),
      };
    });
  }

  const incidents = new Map<string, Incident[]>();
  /**
   * MONITORING-P2: the company's first DE crossed 80 % of its monthly budget this month (open)
   * and hit its cap last month (resolved by an approval); its second DE had a reservation
   * refused this month.
   */
  function incidentsOf(companyId: string): Incident[] {
    const have = incidents.get(companyId);
    if (have) return have;
    const now = deps.now();
    const month = (back: number) => {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 15));
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    };
    const ago = (h: number) => new Date(now.getTime() - h * 3_600_000).toISOString();
    const [a, b] = deps.desOf(companyId);
    const list: Incident[] = [];
    const base = (d: FakeDe, n: number) => ({
      id: uid(0xe7e4, ++incidentSeq),
      currency: "USD",
      budget_id: uid(0xe7e5, n),
      budget_amount_minor: 50_000,
      budget_period_type: "month" as const,
      budget_subject_kind: "holder" as const,
      de_membership_id: d.membership_id,
      de_name: d.display_name,
      run_id: null,
      resolved_by_approval_id: null,
      resolved_at: null,
    });
    if (a)
      list.push(
        {
          ...base(a, 1),
          kind: "threshold",
          threshold: "0.800",
          period_key: month(0),
          amount_minor: 40_250,
          created_at: ago(5),
        },
        {
          ...base(a, 1),
          kind: "cap",
          threshold: null,
          period_key: month(1),
          amount_minor: 50_000,
          resolved_by_approval_id: uid(0xe7e6, 1),
          resolved_at: ago(24 * 20),
          created_at: ago(24 * 21),
        },
      );
    if (b)
      list.push({
        ...base(b, 2),
        kind: "reservation_failed",
        threshold: null,
        period_key: month(0),
        amount_minor: 1_200,
        budget_amount_minor: 20_000,
        created_at: ago(2),
      });
    list.sort((x, y) => y.created_at.localeCompare(x.created_at));
    incidents.set(companyId, list);
    return list;
  }

  const gate = (p: Principal | null): Response | null => {
    if (!p) return notFound();
    if (!deps.agentsOn(p)) return problem(403, "feature_disabled", "Feature not enabled");
    return null;
  };
  const body = (b: unknown) => (b && typeof b === "object" ? (b as Record<string, unknown>) : {});
  const PATTERN = /^\S{1,200}$/;
  const UUID = /^[0-9a-f-]{36}$/i;

  const handlers: Partial<Record<RouteId, Handler>> = {
    "runs.telemetry": (ctx) => {
      const no = gate(ctx.principal);
      if (no || !ctx.principal) return no ?? notFound();
      const run = deps.runFor(ctx, ctx.params.id ?? "");
      if (run instanceof Response) return run;
      const list = seedEvents(run);
      const stopList = advance(run);
      const evs = [...list].sort((x, y) => x.occurred_at.localeCompare(y.occurred_at));
      const job = foldJob(evs, run);
      return json({
        run_id: run.id,
        events: evs.slice(0, 500),
        truncated: evs.length > 500,
        jobs: job ? [job] : [],
        stop_requests: [...stopList].reverse(),
        alerts: seedAlerts(ctx.principal.company.id).filter((a) => a.run_id === run.id),
      } satisfies Telemetry);
    },
    "runs.requestStop": (ctx) => {
      const p = ctx.principal;
      const no = gate(p);
      if (no || !p) return no ?? notFound();
      const run = deps.runFor(ctx, ctx.params.id ?? "");
      if (run instanceof Response) return run;
      if (ENDED.has(run.state))
        return problem(409, "run_state_conflict", "Run already ended", `The run is ${run.state}.`);
      const list = advance(run);
      const open = list.find((s) => s.state === "pending" || s.state === "picked");
      if (open) return json(open);
      const reason = body(ctx.body).reason;
      const now = deps.now();
      const s: Stop = {
        id: uid(0xe7e1, ++stopSeq),
        run_id: run.id,
        de_membership_id: run.membership_id,
        source: "person",
        requested_by_membership_id:
          ctx.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null,
        guard_rule_id: null,
        reason: typeof reason === "string" && reason.trim() ? reason.trim() : "stopped by a person",
        state: "pending",
        requested_at: now.toISOString(),
        expires_at: new Date(now.getTime() + EXPIRE_MS).toISOString(),
        picked_at: null,
        acked_at: null,
        ack: null,
      };
      list.push(s);
      stops.set(run.id, list);
      return json(s);
    },
    "monitoring.alerts.list": ({ principal: p, url }) => {
      const no = gate(p);
      if (no || !p) return no ?? notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers see the alerts");
      const q = url.searchParams;
      const state = q.get("state") ?? "active";
      const kind = q.get("kind");
      const de = q.get("de_membership_id");
      const limit = Math.min(200, Number(q.get("limit") ?? 50) || 50);
      const rows = seedAlerts(p.company.id)
        .filter((a) =>
          state === "all" ? true : state === "active" ? a.state !== "resolved" : a.state === state,
        )
        .filter((a) => !kind || a.kind === kind)
        .filter((a) => !de || a.de_membership_id === de)
        .sort((x, y) => y.last_seen_at.localeCompare(x.last_seen_at))
        .slice(0, limit);
      return json({ data: rows });
    },
    "monitoring.alerts.update": ({ principal: p, params, body: raw, me }) => {
      const no = gate(p);
      if (no || !p) return no ?? notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers ack alerts");
      const a = seedAlerts(p.company.id).find((x) => x.id === params.id);
      if (!a) return notFound();
      const state = body(raw).state;
      if (state !== "acked" && state !== "resolved") return validation("state: acked or resolved");
      const at = deps.now().toISOString();
      if (state === "acked" && a.state === "open")
        Object.assign(a, {
          state: "acked",
          acked_at: at,
          acked_by_membership_id:
            me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null,
        });
      if (state === "resolved" && a.state !== "resolved")
        Object.assign(a, {
          state: "resolved",
          resolved_at: at,
          resolved_how: "resolved by a person",
        });
      return json(a);
    },
    "monitoring.fleet": ({ principal: p }) => {
      const no = gate(p);
      if (no || !p) return no ?? notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers see the fleet");
      return json({
        data: fleetOf(p.company.id),
        silent_after_minutes: 30,
      } satisfies OperationOutput<"monitoring.fleet">);
    },
    "monitoring.budgetIncidents": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!p.manager)
        return problem(403, "forbidden", "Forbidden", "Managers see the budget incidents");
      const q = url.searchParams;
      const de = q.get("de_membership_id");
      const period = q.get("period_key");
      const state = q.get("state") ?? "all";
      const rows = incidentsOf(p.company.id)
        .filter((i) => !de || i.de_membership_id === de)
        .filter((i) => !period || i.period_key === period)
        .filter((i) =>
          state === "open"
            ? i.resolved_at === null
            : state === "resolved"
              ? i.resolved_at !== null
              : true,
        );
      return json({ data: rows } satisfies OperationOutput<"monitoring.budgetIncidents">);
    },
    "operator.guards.list": ({ me }) => {
      const no = deps.refuseOperator(me);
      if (no) return no;
      return json({ data: guards });
    },
    "operator.guards.create": ({ me, body: raw }) => {
      const no = deps.refuseOperator(me);
      if (no) return no;
      const b = body(raw);
      if (b.match_kind !== "thread" && b.match_kind !== "tool")
        return validation("match_kind: thread or tool");
      if (typeof b.pattern !== "string" || !PATTERN.test(b.pattern))
        return validation("pattern: 1 to 200 characters, no spaces");
      const company =
        typeof b.company_id === "string" && UUID.test(b.company_id) ? b.company_id : null;
      const de =
        typeof b.de_membership_id === "string" && UUID.test(b.de_membership_id)
          ? b.de_membership_id
          : null;
      if (de && !company) return validation("company_id: a DE rule names its company");
      const note = typeof b.note === "string" && b.note.trim() ? b.note.trim().slice(0, 300) : null;
      const at = deps.now().toISOString();
      const g = guard({
        id: uid(0xe7e3, ++guardSeq),
        company_id: company,
        de_membership_id: de,
        match_kind: b.match_kind,
        pattern: b.pattern,
        note,
        created_by_user_id: me.user.id,
        updated_by_user_id: me.user.id,
        created_at: at,
        updated_at: at,
      });
      guards.push(g);
      return json(g, 201);
    },
    "operator.guards.update": ({ me, params, body: raw }) => {
      const no = deps.refuseOperator(me);
      if (no) return no;
      const g = guards.find((x) => x.id === params.id);
      if (!g) return notFound();
      const b = body(raw);
      if ("pattern" in b) {
        if (typeof b.pattern !== "string" || !PATTERN.test(b.pattern))
          return validation("pattern: 1 to 200 characters, no spaces");
        if (g.builtin && b.pattern !== g.pattern)
          return validation("pattern: a built-in rule's pattern is fixed");
      }
      if ("action" in b && b.action !== "alert" && b.action !== "stop_and_alert")
        return validation("action: alert or stop_and_alert");
      if ("enabled" in b && typeof b.enabled !== "boolean") return validation("enabled: boolean");
      const next = {
        ...(typeof b.pattern === "string" ? { pattern: b.pattern } : {}),
        ...(b.action === "alert" || b.action === "stop_and_alert" ? { action: b.action } : {}),
        ...(typeof b.enabled === "boolean" ? { enabled: b.enabled } : {}),
        ...("note" in b
          ? {
              note:
                typeof b.note === "string" && b.note.trim() ? b.note.trim().slice(0, 300) : null,
            }
          : {}),
      };
      const changed = (Object.keys(next) as (keyof typeof next)[]).some((k) => g[k] !== next[k]);
      if (changed)
        Object.assign(g, next, {
          version: g.version + 1,
          updated_by_user_id: me.user.id,
          updated_at: deps.now().toISOString(),
        });
      return json(g);
    },
  };

  return {
    handlers,
    /** Answers a run's live stop request as its bridge would (tests; the demo plays it by time). */
    answerStop(runId: string, ok: boolean): boolean {
      const list = stops.get(runId) ?? [];
      const s = list.find((x) => x.state === "pending" || x.state === "picked");
      const run = seen.get(runId);
      if (!s || !run) return false;
      ack(run, s, ok, deps.now());
      return true;
    },
  };
}
